// 치과 「환자 자동관리 › 알림」 /patients/alerts (2026-10-08 구현 — 기획서 docs/기획서_환자자동관리_알림파트_2026-10-07.md §4, 대표 Q1~Q9 권고대로 승인)
//   구성(위→아래): A 검진 시기 안내(플래그 clinicRecall) · B 치과 안내 보내기(플래그 clinicNotify) · C 현황(링크 + 보낸 안내 표)
//   권한(Q2): 기본 설정 저장·치과 안내 미리보기·예약·취소 = 치과 대표만 — 직원은 버튼 비활성 + 이유 표시. 일괄 리콜 예약은 직원도.
//   금지: 자유 문구 입력란·즉시 발송·재발송·내보내기·인쇄·복사·빨강 경보·행동 권유·할인/후기 등 의료법 금지 표현.
//   서버 계약(dev-server): docs/계약_알림파트_서버_2026-10-08.md — 화면은 services/clinicRecallApi·clinicAlertsApi 래퍼만 쓴다.
//   라우트 게이트: App.js (치과 계정 && isClinicAlertsOn). 블록 A/B 는 각 플래그로 따로 숨긴다 — OFF 블록은 CF 호출 0.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Bell, CalendarClock, Send, ListChecks, ArrowLeft, RefreshCw } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { unifiedStyles as U } from '../../styles/Styles';
import { getFeatures, isClinicRecallOn, isClinicNotifyOn } from '../../utils/features';
import {
    TRIGGER_CODES, triggerLabel, TRIGGER_DESC, RECALL_RULE_NOTICE, RECALL_BULK_MAX, BASIS_LOOKBACK_DAYS, DUE_MIN_DAYS,
    getClinicRecallDefaults, setClinicRecallDefaults, setPatientRecallsBulk, previewPatientRecallsBulk, normalizeTriggerCatalog, normalizeRecallDefaults,
    periodLabel, bulkSkipLabel, recallPrecheck, describeRecallBulkError, describeRecallDefaultsError, clinicSendBlockers, dayKey, addMonths, daysAhead, fmtDay,
} from '../../services/clinicRecallApi';
import {
    NOTICE_TEMPLATES, noticeTemplateLabel, PREPARE_ITEMS, PREPARE_MAX, AUDIENCE_MODES, RECENT_MONTH_OPTIONS, DEFAULT_RECENT_MONTHS, PICK_MAX,
    SEND_MAX_AHEAD_DAYS, SEND_TIME_TEXT, LIMITS_TEXT, TIME_OPTIONS, VAR_MAX_AHEAD_DAYS, CLOSED_MAX_SPAN_DAYS, REOPEN_MAX_AFTER_DAYS,
    validateNoticeVarsLocal, validateSendDayLocal, buildNoticeVars, buildAudience, kstNow, earliestSendDay,
    previewClinicNotice, scheduleClinicNotice, cancelClinicNotice, listClinicNotices, describeNotifyError, addDaysKey,
} from '../../services/clinicAlertsApi';
import ClinicPatientMultiPicker from './ClinicPatientMultiPicker';
import RecallSendBlockers from './RecallSendBlockers';

const OWNER_ONLY_TEXT = '치과 대표만 할 수 있습니다. (직원 계정은 보기만 가능)';
// 발송 가능 여부 라벨 — 계약 A-3 dryRun decision(+ fcmRegistered:false = 앱 없음). 명부 사전 표시(recallPrecheck)도 같은 사전
const PRECHECK_TEXT = Object.freeze({ ok: '발송 가능', no_consent: '재내원 안내 동의 없음', consent_expired: '재내원 안내 동의 없음', notify_off: '알림 꺼짐', no_optin: '재내원 안내 수신 꺼짐', no_device: '앱 없음', inactive: '등록 상태 아님', not_found: '환자 정보 없음' });
const decisionText = (it) => (it.decision === 'ok' && it.fcmRegistered === false) ? PRECHECK_TEXT.no_device : (PRECHECK_TEXT[it.decision] || it.decision || '-');

const sectionTitle = { margin: '0 0 4px', fontSize: 16, fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 6 };
const subTitle = { margin: '0 0 6px', fontSize: 14, fontWeight: 700, color: '#0f172a' };
const label = { fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6, display: 'block' };
const input = { padding: '8px 10px', fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff' };
const errBox = { background: '#fee2e2', color: '#b91c1c', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.5 };
const okBox = { background: '#ecfdf5', color: '#065f46', border: '1px solid #a7f3d0', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginTop: 8, lineHeight: 1.5 };
const infoBox = { background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, lineHeight: 1.6 };
const ownerNote = { fontSize: 12.5, color: '#64748b', marginTop: 6 };
const thStyle = { ...U.tableTh, padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
const tdStyle = { ...U.tableTd, padding: '8px 10px', color: '#334155', verticalAlign: 'top' };
const chip = (active) => ({ ...U.buttonSecondary, padding: '5px 12px', fontSize: 13, background: active ? '#4f46e5' : '#fff', color: active ? '#fff' : '#334155', borderColor: active ? '#4f46e5' : '#e2e8f0' });
const disabledStyle = (d) => (d ? { opacity: 0.55, cursor: 'not-allowed' } : {});

// in-flight 가드(검토 P2-9) — state(busy)는 다음 렌더 전까지 반영되지 않아 빠른 연타가 두 번 호출될 수 있다. ref 로 즉시 잠근다
function useInFlight() {
    const ref = useRef(false);
    return useCallback(async (fn) => {
        if (ref.current) return undefined;
        ref.current = true;
        try { return await fn(); } finally { ref.current = false; }
    }, []);
}

// 1분 주기 재렌더(검토 P2-10a) — "오늘"·"가장 이른 발송일"(10:15 KST 경계)을 화면을 오래 열어 둬도 맞게 다시 계산
function useMinuteTick() {
    const [tick, setTick] = useState(0);
    useEffect(() => { const t = setInterval(() => setTick(n => n + 1), 60 * 1000); return () => clearInterval(t); }, []);
    return tick;
}

// basisDay + 주기 → 예정일(로컬 날짜 — 서버도 KST 기준, 사용 전제 한국)
function dueDayOf(basisDay, unit, period) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(basisDay || ''));
    if (!m || !Number.isFinite(period)) return '';
    const base = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (unit === 'months') return dayKey(addMonths(base, period));
    const days = unit === 'weeks' ? period * 7 : period;
    return dayKey(new Date(base.getFullYear(), base.getMonth(), base.getDate() + days));
}

// ─────────────────────────────────────────────────────────────────────────────
// A-1 기본 설정 카드 (대표만 저장)
// ─────────────────────────────────────────────────────────────────────────────
function RecallDefaultsCard({ isOwner, state, onSaved }) {
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    useEffect(() => { if (state.data) setDraft(state.data.defaults); }, [state.data]);

    const catalog = state.data?.catalog || normalizeTriggerCatalog(null);
    const canEdit = isOwner && state.data?.canEdit !== false;
    const dirty = !!(draft && state.data && JSON.stringify(draft) !== JSON.stringify(state.data.defaults));

    const setRow = (code, patch) => { setSaved(''); setDraft(d => ({ ...d, [code]: { ...d[code], ...patch } })); };
    const guard = useInFlight();
    const save = () => guard(async () => {
        if (!canEdit || busy || !draft) return;
        setBusy(true); setError(''); setSaved('');
        try {
            const res = await setClinicRecallDefaults(draft);
            if (res?.ok === false) throw new Error('응답이 올바르지 않습니다');
            setSaved('저장했습니다. 새로 등록하는 안내부터 적용됩니다(이미 예약된 안내는 그대로).');
            onSaved && onSaved();
        } catch (e) {
            setError(describeRecallDefaultsError(e, '기본 설정을 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusy(false); }
    });

    return (
        <div style={{ ...U.card, marginBottom: 12 }}>
            <h3 style={subTitle}>{'기본 설정'}</h3>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>{'안내 종류별로 켜기와 기본 주기를 정합니다. 환자 상세의 [다음 안내 시기]와 아래 일괄 예약의 첫 값으로 쓰이고, 꺼 둔 종류는 선택지에서 빠집니다.'}</div>
            {state.loading && <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>불러오는 중...</div>}
            {state.error && (
                <div style={errBox}>{state.error} <button type="button" onClick={state.reload} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, marginLeft: 6 }}>다시 시도</button></div>
            )}
            {draft && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr><th style={thStyle}>종류</th><th style={thStyle}>켜기</th><th style={thStyle}>기본 주기</th><th style={thStyle}>설명</th></tr>
                        </thead>
                        <tbody>
                            {TRIGGER_CODES.map(code => {
                                const c = catalog[code];
                                const row = draft[code] || { enabled: true, period: c.defaultPeriod };
                                return (
                                    <tr key={code}>
                                        <td style={{ ...tdStyle, fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{triggerLabel(code)}</td>
                                        <td style={tdStyle}>
                                            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, ...disabledStyle(!canEdit) }}>
                                                <input type="checkbox" role="switch" aria-checked={row.enabled} aria-label={`${triggerLabel(code)} 켜기`}
                                                    checked={row.enabled} disabled={!canEdit || busy} onChange={(e) => setRow(code, { enabled: e.target.checked })} />
                                                {row.enabled ? '켜짐' : '꺼짐'}
                                            </label>
                                        </td>
                                        <td style={tdStyle}>
                                            <select value={row.period} disabled={!canEdit || busy || !row.enabled} aria-label={`${triggerLabel(code)} 기본 주기`}
                                                onChange={(e) => setRow(code, { period: Number(e.target.value) })} style={{ ...input, padding: '6px 8px', ...disabledStyle(!canEdit) }}>
                                                {c.allowed.map(n => <option key={n} value={n}>{periodLabel(c.unit, n)}</option>)}
                                            </select>
                                        </td>
                                        <td style={{ ...tdStyle, fontSize: 12.5, color: '#64748b' }}>{TRIGGER_DESC[code] || ''}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {draft && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
                    <button type="button" onClick={save} disabled={!canEdit || busy || !dirty} title={!canEdit ? OWNER_ONLY_TEXT : undefined}
                        style={{ ...U.buttonPrimary, ...(!canEdit || busy || !dirty ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>{busy ? '저장 중...' : '기본 설정 저장'}</button>
                    {!canEdit && <span style={ownerNote}>{OWNER_ONLY_TEXT}</span>}
                </div>
            )}
            {error && <div style={errBox}>{error}</div>}
            {saved && <div style={okBox}>{saved}</div>}
            <div style={{ ...infoBox, marginTop: 12 }}>
                <div>{'환자가 앱에서 재내원 안내 받기를 켠 경우에만 발송됩니다. 발송 시각·문구·횟수는 바꿀 수 없습니다.'}</div>
                <div style={{ marginTop: 4 }}>{RECALL_RULE_NOTICE}</div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// A-2 대상 환자 선택·일괄 예약 (대표·직원)
// ─────────────────────────────────────────────────────────────────────────────
function RecallBulkCard({ clinicId, state, onBlockers }) {
    const catalog = state.data?.catalog || normalizeTriggerCatalog(null);
    const defaults = state.data?.defaults || normalizeRecallDefaults(null, catalog);
    const enabledCodes = TRIGGER_CODES.filter(c => defaults[c]?.enabled !== false);

    const [pickerOpen, setPickerOpen] = useState(false);
    const [picked, setPicked] = useState([]);           // normalizeRecord 행
    const [trigger, setTrigger] = useState('');
    const [basisDay, setBasisDay] = useState(() => dayKey(new Date()));
    const [period, setPeriod] = useState(null);
    const [busy, setBusy] = useState('');               // '' | 'preview' | 'submit'
    const [error, setError] = useState('');
    const [preview, setPreview] = useState(null);       // 계약 A-3 dryRun 응답 — 입력이 바뀌면 무효
    const [result, setResult] = useState(null);         // { done[], skipped[], failed[] } + 이름 사전

    // 트리거 기본값 반영 — 기본 설정이 로드되거나 트리거가 바뀌면 주기를 기본 주기로
    useEffect(() => {
        if (!enabledCodes.length) { setTrigger(''); return; }
        if (!trigger || !enabledCodes.includes(trigger)) setTrigger(enabledCodes[0]);
    }, [enabledCodes.join(','), trigger]);   // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!trigger) return;
        setPeriod(defaults[trigger]?.period ?? catalog[trigger]?.defaultPeriod ?? null);
    }, [trigger, state.data]);   // eslint-disable-line react-hooks/exhaustive-deps

    useMinuteTick();                 // 기준일 범위(오늘~90일 전)도 날짜가 바뀌면 다시 계산(검토 P2-10a)
    const today = new Date();
    const minBasis = dayKey(new Date(today.getFullYear(), today.getMonth(), today.getDate() - BASIS_LOOKBACK_DAYS));
    const maxBasis = dayKey(today);
    const unit = catalog[trigger]?.unit || 'months';
    const dueDay = trigger && period ? dueDayOf(basisDay, unit, period) : '';
    const ahead = daysAhead(dueDay);
    const dueOk = Number.isFinite(ahead) && ahead >= DUE_MIN_DAYS;
    const basisOk = basisDay >= minBasis && basisDay <= maxBasis;

    // 입력이 바뀌면 미리보기 무효(미리 본 결과와 다른 것을 예약하지 않게)
    useEffect(() => { setPreview(null); }, [picked, trigger, basisDay, period]);
    const guard = useInFlight();

    const statusOf = useCallback((r) => {
        const p = recallPrecheck(r);
        return { selectable: p.selectable, text: PRECHECK_TEXT[p.reason] || '' };
    }, []);

    const inputErr = () => {
        if (!basisOk) return `기준일은 오늘부터 ${BASIS_LOOKBACK_DAYS}일 전까지 고를 수 있습니다.`;
        if (!dueOk) return `안내 예정일이 오늘부터 ${DUE_MIN_DAYS}일 이후여야 합니다. 기준일이나 주기를 바꿔 주세요.`;
        return '';
    };
    // [미리보기] — 서버 dryRun(저장 0)으로 환자별 발송 가능 여부를 받는다. 접속기록 recall_bulk_preview
    const doPreview = () => guard(async () => {
        if (busy || !picked.length || !trigger || !period) return;
        const ie = inputErr(); if (ie) { setError(ie); return; }
        setBusy('preview'); setError(''); setResult(null);
        try {
            const p = await previewPatientRecallsBulk({ pids: picked.map(r => r.id), trigger, basisDay, period });
            const seen = new Set(p.items.map(x => x.pid));
            const missing = picked.filter(r => !seen.has(r.id)).length;
            if (missing) console.error('[알림 파트] 일괄 미리보기 응답에 없는 환자 수:', missing);
            setPreview({ ...p, missing });
        } catch (e) {
            setError(describeRecallBulkError(e, '미리보기를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusy(''); }
    });
    const selectablePids = preview ? preview.items.filter(x => x.selectable).map(x => x.pid) : [];

    const submit = () => guard(async () => {
        if (busy || !preview || !selectablePids.length || !trigger || !period) return;
        const ie = inputErr(); if (ie) { setError(ie); return; }
        setBusy('submit'); setError(''); setResult(null);
        try {
            // 동의 없는 환자(selectable:false)는 보내지 않는다 — 리콜 문서 자체를 만들지 않음(기획 §4-1)
            const res = await setPatientRecallsBulk({ pids: selectablePids, trigger, basisDay, period });
            const names = new Map(picked.map(r => [r.id, r.name || '(이름 없음)']));
            // 응답에 빠진 환자(어느 목록에도 없음)도 숨기지 않고 「결과 미확인」으로 표시 — 조용한 실패 금지
            const seen = new Set([...res.done, ...res.skipped, ...res.failed].map(x => x.pid));
            const unknown = selectablePids.filter(pid => !seen.has(pid)).map(pid => ({ pid }));
            if (unknown.length) console.error('[알림 파트] 일괄 예약 응답에 없는 환자 수:', unknown.length);
            const excluded = preview.items.filter(x => !x.selectable).map(x => ({ pid: x.pid, reason: x.decision }));
            setResult({ ...res, unknown, excluded, names });
            onBlockers && onBlockers(res.blockers);
            setPicked([]); setPreview(null);
        } catch (e) {
            setError(describeRecallBulkError(e, '일괄 예약을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusy(''); }
    });

    const nameOf = (pid) => result?.names?.get(pid) || '(이름 없음)';

    return (
        <div style={{ ...U.card, marginBottom: 12 }}>
            <h3 style={subTitle}>{'대상 환자 고르기 · 일괄 예약'}</h3>
            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10, lineHeight: 1.5 }}>
                {`명부에서 환자를 골라 같은 종류의 안내를 한 번에 예약합니다(1회 최대 ${RECALL_BULK_MAX}명). 바로 보내지 않고, 예정일에 맞춰 정해진 시각에 나갑니다. 같은 종류의 진행 중인 안내가 있으면 새 시기로 교체됩니다.`}
            </div>

            {enabledCodes.length === 0 ? (
                <div style={infoBox}>{'기본 설정에서 켜 둔 안내 종류가 없습니다.'}</div>
            ) : (
                <>
                    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
                        <div>
                            <span style={label}>{'안내 종류'}</span>
                            <select value={trigger} onChange={(e) => setTrigger(e.target.value)} disabled={!!busy} style={input} aria-label="안내 종류">
                                {enabledCodes.map(c => <option key={c} value={c}>{triggerLabel(c)}</option>)}
                            </select>
                        </div>
                        <div>
                            <span style={label}>{'기준일(방문·장착일)'}</span>
                            <input type="date" value={basisDay} min={minBasis} max={maxBasis} disabled={!!busy} onChange={(e) => setBasisDay(e.target.value)} style={input} aria-label="기준일" />
                        </div>
                        <div>
                            <span style={label}>{'주기'}</span>
                            <select value={period ?? ''} onChange={(e) => setPeriod(Number(e.target.value))} disabled={!!busy || !trigger} style={input} aria-label="주기">
                                {(catalog[trigger]?.allowed || []).map(n => <option key={n} value={n}>{periodLabel(unit, n)}{n === defaults[trigger]?.period ? ' (기본)' : ''}</option>)}
                            </select>
                        </div>
                    </div>
                    <div style={{ fontSize: 13, color: dueOk ? '#0f172a' : '#b45309', background: '#f8fafc', borderRadius: 8, padding: '8px 10px', marginBottom: 10 }}>
                        {'안내 예정일: '}<b>{dueDay ? fmtDay(dueDay) : '-'}</b>{Number.isFinite(ahead) ? ` (${ahead}일 뒤)` : ''}
                        {!dueOk && dueDay && <div style={{ fontSize: 12, marginTop: 2 }}>{`예정일이 오늘부터 ${DUE_MIN_DAYS}일 이후여야 예약할 수 있습니다.`}</div>}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                        <button type="button" onClick={() => setPickerOpen(true)} disabled={!!busy} style={U.buttonSecondary}><ListChecks size={14} /> {picked.length ? '환자 다시 고르기' : '환자 고르기'}</button>
                        <span style={{ fontSize: 13, color: '#475569' }}>{`선택 ${picked.length}명`}</span>
                    </div>

                    {picked.length > 0 && (
                        <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 8, marginBottom: 10, maxHeight: 320, overflowY: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead><tr><th style={thStyle}>환자명</th><th style={thStyle}>예정일</th><th style={thStyle}>{preview ? '발송 가능 여부' : '발송 가능 여부(명부 기준 — [미리보기]로 확인)'}</th></tr></thead>
                                <tbody>
                                    {picked.map(r => {
                                        const it = preview ? preview.items.find(x => x.pid === r.id) : null;
                                        const excluded = !!it && !it.selectable;
                                        return (
                                            <tr key={r.id} style={excluded ? { color: '#94a3b8' } : undefined}>
                                                <td style={{ ...tdStyle, fontWeight: 600, color: excluded ? '#94a3b8' : '#0f172a' }}>{r.name || '(이름 없음)'}</td>
                                                <td style={tdStyle}>{excluded ? '-' : fmtDay((preview && preview.dueDay) || dueDay)}</td>
                                                <td style={tdStyle}>
                                                    {preview ? (it ? `${decisionText(it)}${excluded ? ' · 예약 제외' : ''}` : '결과 없음(예약 제외)') : (PRECHECK_TEXT[recallPrecheck(r).reason] || '')}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                    {picked.length > 0 && (
                        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 10, lineHeight: 1.5 }}>
                            {preview
                                ? `예약 가능 ${selectablePids.length}명 / 고른 ${picked.length}명${Number.isFinite(preview.dailyUsed) && Number.isFinite(preview.dailyMax) ? ` · 오늘 일괄 예약 ${preview.dailyUsed}/${preview.dailyMax}명` : ''}. 알림 꺼짐·앱 없음 환자도 예약은 되며, 보낼 때 다시 확인해 건너뜁니다.`
                                : '[미리보기]를 누르면 환자별 발송 가능 여부를 확인합니다. 동의 없는 환자는 예약에서 빠집니다.'}
                            {preview?.missing > 0 && <div style={{ color: '#92400e' }}>{`미리보기 응답에 없는 환자 ${preview.missing}명은 예약하지 않습니다.`}</div>}
                        </div>
                    )}

                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <button type="button" onClick={doPreview} disabled={!!busy || !picked.length || !dueOk || !basisOk}
                            style={{ ...U.buttonSecondary, ...((busy || !picked.length || !dueOk || !basisOk) ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>
                            {busy === 'preview' ? '확인 중...' : '미리보기'}
                        </button>
                        <button type="button" onClick={submit} disabled={!!busy || !preview || !selectablePids.length || !dueOk || !basisOk}
                            style={{ ...U.buttonPrimary, ...((busy || !preview || !selectablePids.length || !dueOk || !basisOk) ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>
                            <CalendarClock size={14} /> {busy === 'submit' ? '예약 중...' : `${selectablePids.length}명 예약`}
                        </button>
                    </div>
                </>
            )}
            {error && <div style={errBox}>{error}</div>}

            {result && (
                <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 6 }}>
                        {`예약 ${result.done.length}건 · 건너뜀 ${result.skipped.length + result.excluded.length}건 · 실패 ${result.failed.length}건${result.unknown.length ? ` · 결과 미확인 ${result.unknown.length}건` : ''}`}
                        {result.dueDay ? <span style={{ fontWeight: 400, color: '#475569' }}>{` · 예정일 ${fmtDay(result.dueDay)}`}</span> : null}
                    </div>
                    <div style={{ overflowX: 'auto', border: '1px solid #f1f5f9', borderRadius: 8, maxHeight: 320, overflowY: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr><th style={thStyle}>환자명</th><th style={thStyle}>결과</th><th style={thStyle}>사유·예정일</th></tr></thead>
                            <tbody>
                                {result.done.map(x => <tr key={`d_${x.pid}`}><td style={tdStyle}>{nameOf(x.pid)}</td><td style={tdStyle}>{'예약됨'}</td><td style={tdStyle}>{result.dueDay ? fmtDay(result.dueDay) : ''}</td></tr>)}
                                {result.excluded.map(x => <tr key={`x_${x.pid}`}><td style={tdStyle}>{nameOf(x.pid)}</td><td style={tdStyle}>{'건너뜀'}</td><td style={tdStyle}>{bulkSkipLabel(x.reason)}</td></tr>)}
                                {result.skipped.map(x => <tr key={`s_${x.pid}`}><td style={tdStyle}>{nameOf(x.pid)}</td><td style={tdStyle}>{'건너뜀'}</td><td style={tdStyle}>{bulkSkipLabel(x.reason)}</td></tr>)}
                                {result.failed.map(x => <tr key={`f_${x.pid}`}><td style={tdStyle}>{nameOf(x.pid)}</td><td style={tdStyle}>{'실패'}</td><td style={tdStyle}>{bulkSkipLabel(x.reason || 'error')}</td></tr>)}
                                {result.unknown.map(x => <tr key={`u_${x.pid}`}><td style={tdStyle}>{nameOf(x.pid)}</td><td style={tdStyle}>{'결과 미확인'}</td><td style={tdStyle}>{'재내원 안내 화면에서 확인해 주세요'}</td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {pickerOpen && (
                <ClinicPatientMultiPicker clinicId={clinicId} title="일괄 예약할 환자 고르기" max={RECALL_BULK_MAX} statusOf={statusOf}
                    initialSelectedIds={picked.map(r => r.id)}
                    hint="재내원 안내 동의가 없는 환자는 고를 수 없습니다. 동의는 환자가 앱에서 직접 켭니다."
                    onClose={() => setPickerOpen(false)} onConfirm={(rows) => { setPicked(rows); setResult(null); setError(''); setPickerOpen(false); }} />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// B 치과 안내 보내기 (대표만 — 직원은 비활성 + 이유)
// ─────────────────────────────────────────────────────────────────────────────
function NoticeVarsForm({ code, vars, setVars, disabled, todayKey, prepareOptions }) {
    const set = (patch) => setVars(v => ({ ...v, ...patch }));
    const maxKey = addDaysKey(todayKey, VAR_MAX_AHEAD_DAYS);
    const timeSelect = (key, aria, allowEmpty) => (
        <select value={vars[key] || ''} disabled={disabled} onChange={(e) => set({ [key]: e.target.value })} style={input} aria-label={aria}>
            <option value="">{allowEmpty ? '없음' : '선택'}</option>
            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
    );
    const dateInput = (key, aria, min = todayKey, max = maxKey) => (
        <input type="date" value={vars[key] || ''} min={min} max={max} disabled={disabled} onChange={(e) => set({ [key]: e.target.value })} style={input} aria-label={aria} />
    );
    const row = { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' };

    if (code === 'notice_closed') {
        return (
            <div style={row}>
                <div><span style={label}>{'휴진 시작일'}</span>{dateInput('from', '휴진 시작일')}</div>
                <div><span style={label}>{'휴진 마지막 날'}</span>{dateInput('to', '휴진 마지막 날', vars.from || todayKey, vars.from ? addDaysKey(vars.from, CLOSED_MAX_SPAN_DAYS) : maxKey)}</div>
                <div><span style={label}>{'진료 재개일(비우면 다음 날)'}</span>{dateInput('reopen', '진료 재개일', vars.to ? addDaysKey(vars.to, 1) : todayKey, vars.to ? addDaysKey(vars.to, REOPEN_MAX_AFTER_DAYS) : maxKey)}</div>
            </div>
        );
    }
    if (code === 'notice_hours') {
        return (
            <div style={row}>
                <div><span style={label}>{'바뀌는 날짜'}</span>{dateInput('from', '바뀌는 날짜')}</div>
                <div><span style={label}>{'진료 시작'}</span>{timeSelect('open', '진료 시작')}</div>
                <div><span style={label}>{'진료 종료'}</span>{timeSelect('close', '진료 종료')}</div>
                <div><span style={label}>{'점심시간 시작(선택)'}</span>{timeSelect('lunchStart', '점심시간 시작', true)}</div>
                <div><span style={label}>{'점심시간 종료(선택)'}</span>{timeSelect('lunchEnd', '점심시간 종료', true)}</div>
            </div>
        );
    }
    if (code === 'notice_visit') {
        return (
            <div style={row}>
                <div><span style={label}>{'방문 날짜'}</span>{dateInput('visitDate', '방문 날짜')}</div>
                <div><span style={label}>{'방문 시각(선택)'}</span>{timeSelect('visitTime', '방문 시각', true)}</div>
            </div>
        );
    }
    if (code === 'notice_prepare') {
        const items = Array.isArray(vars.prepare) ? vars.prepare : [];   // 계약 B-0 변수 키 'prepare'
        const toggle = (c) => set({ prepare: items.includes(c) ? items.filter(x => x !== c) : (items.length >= PREPARE_MAX ? items : [...items, c]) });
        return (
            <div>
                <span style={label}>{`가져올 것(최대 ${PREPARE_MAX}개)`}</span>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(prepareOptions && prepareOptions.length ? prepareOptions : PREPARE_ITEMS).map(it => {
                        const on = items.includes(it.code);
                        const full = !on && items.length >= PREPARE_MAX;
                        return (
                            <label key={it.code} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, border: '1px solid #e2e8f0', borderRadius: 8, padding: '6px 10px', background: on ? '#eef2ff' : '#fff', ...disabledStyle(disabled || full) }}>
                                <input type="checkbox" checked={on} disabled={disabled || full} onChange={() => toggle(it.code)} />{it.label}
                            </label>
                        );
                    })}
                </div>
            </div>
        );
    }
    if (code === 'notice_contact') {
        return (
            <div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                    <button type="button" disabled={disabled} onClick={() => set({ kind: 'phone' })} style={{ ...chip(vars.kind === 'phone'), ...disabledStyle(disabled) }}>{'대표전화 변경'}</button>
                    <button type="button" disabled={disabled} onClick={() => set({ kind: 'move' })} style={{ ...chip(vars.kind === 'move'), ...disabledStyle(disabled) }}>{'치과 이전'}</button>
                </div>
                {vars.kind === 'phone' && <div style={infoBox}>{'설정 › 프로필에 등록·확인된 대표전화가 들어갑니다. 여기서 번호를 입력하지 않습니다.'} <Link to="/profile" style={{ color: '#4338ca', fontWeight: 600 }}>{'프로필 확인'}</Link></div>}
                {vars.kind === 'move' && (
                    <>
                        <div style={{ ...infoBox, marginBottom: 8 }}>{'설정 › 프로필에 등록된 주소(간략형)가 들어갑니다. 주소를 먼저 새 주소로 바꿔 두세요.'} <Link to="/profile" style={{ color: '#4338ca', fontWeight: 600 }}>{'프로필 확인'}</Link></div>
                        <div><span style={label}>{'이전 날짜'}</span>{dateInput('from', '이전 날짜')}</div>
                    </>
                )}
            </div>
        );
    }
    return null;
}

function NoticeSendCard({ clinicId, isOwner, listState, onScheduled }) {
    // 서버 기준(KST) 오늘·가장 이른 발송일 — 렌더마다 + 1분 주기로 다시 계산(10:15 넘으면 내일부터, 검토 P2-10a)
    useMinuteTick();
    const todayKey = kstNow().dayKey;
    const earliest = earliestSendDay();
    const guard = useInFlight();
    const [code, setCode] = useState('');
    const [vars, setVars] = useState({});
    const [mode, setMode] = useState('recent');
    const [months, setMonths] = useState(DEFAULT_RECENT_MONTHS);
    const [picked, setPicked] = useState([]);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [sendDay, setSendDay] = useState('');
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState('');       // '' | 'preview' | 'schedule'
    const [error, setError] = useState('');
    const [done, setDone] = useState('');

    const listData = listState.data;
    const canSend = isOwner && listData?.canSend !== false;
    const paBlocked = listData?.delegationOk === false;          // 계약 B-4 gate.delegationOk — 위탁 확인 없음
    const nameBlocked = (listData?.blockers || []).length > 0;   // 치과명 미등록·금지어 → 예약 거부(no-sender·clinic-name-banned)
    const locked = !canSend || paBlocked || nameBlocked;
    const isVisit = code === 'notice_visit';

    // 입력이 바뀌면 미리보기 무효(미리본 내용과 다른 것을 예약하지 않게)
    useEffect(() => { setPreview(null); }, [code, vars, mode, months, picked, sendDay]);
    // 종류가 바뀌면 변수·대상 초기화 — 예약 확인은 개별 1명, 그 밖은 기본 대상 = 최근 12개월 방문(대표 결정 Q3)
    useEffect(() => {
        setVars(code === 'notice_contact' ? { kind: 'phone' } : {});
        setMode(code === 'notice_visit' ? 'pick' : 'recent');
        setMonths(DEFAULT_RECENT_MONTHS);
        setPicked([]);
    }, [code]);

    const varsErr = code ? validateNoticeVarsLocal(code, vars, todayKey) : '안내 종류를 고르세요.';
    const audienceErr = mode === 'pick' && picked.length === 0 ? '대상 환자를 고르세요.' : (isVisit && picked.length !== 1 ? '예약 확인은 환자 1명만 고를 수 있습니다.' : '');
    const maxSend = addDaysKey(earliest, SEND_MAX_AHEAD_DAYS);
    const sendErr = validateSendDayLocal(code, vars, sendDay, earliest);
    const closedLate = code === 'notice_closed' && vars.from && sendDay && addDaysKey(sendDay, 3) > vars.from;   // 휴진 시작 3일 전 이상 권장(기획 B-2 각주)
    const formErr = varsErr || audienceErr || sendErr;

    const payload = () => ({ templateCode: code, vars: buildNoticeVars(code, vars), audience: buildAudience({ mode, months, pids: picked.map(r => r.id) }), sendDay });

    const doPreview = () => guard(async () => {
        if (locked || busy || formErr) return;
        setBusy('preview'); setError(''); setDone('');
        try {
            const p = await previewClinicNotice(payload());
            if (!p.ok) throw new Error('미리보기 응답이 올바르지 않습니다');
            setPreview(p);
        } catch (e) {
            setError(describeNotifyError(e, '미리보기를 만들지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusy(''); }
    });
    const doSchedule = () => guard(async () => {
        if (locked || busy || formErr || !preview || preview.capBlocks.length > 0) return;
        setBusy('schedule'); setError(''); setDone('');
        try {
            const r = await scheduleClinicNotice(payload());
            if (!r.ok) throw new Error('예약 응답이 올바르지 않습니다');
            setDone(`${fmtDay(r.sendDay || sendDay)} ${SEND_TIME_TEXT}에 「${noticeTemplateLabel(code)}」 안내를 보내도록 예약했습니다. 아래 「보낸 안내」에서 확인·취소할 수 있습니다.`);
            setPreview(null); setCode(''); setVars({}); setPicked([]); setSendDay(''); setMode('recent'); setMonths(DEFAULT_RECENT_MONTHS);
            onScheduled && onScheduled();
        } catch (e) {
            setError(describeNotifyError(e, '예약하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusy(''); }
    });

    const step = (n, title) => <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', margin: '14px 0 8px' }}>{`${n}. ${title}`}</div>;
    const pickStatus = useCallback((r) => {
        if (!r.notifyOn) return { selectable: true, text: '알림 꺼짐' };
        if (r.appNotMissing === false) return { selectable: true, text: '앱 없음' };   // 서버 기준(appNotMissing)
        return { selectable: true, text: '' };
    }, []);

    return (
        <div style={{ ...U.card, marginBottom: 12 }}>
            <h3 style={subTitle}>{'안내 보내기'}</h3>
            <div style={{ fontSize: 12.5, color: '#64748b', lineHeight: 1.6 }}>
                {`정해진 문안 5종 중에서 고르고 날짜·시간만 넣습니다(문구를 직접 쓰지 않습니다). 앱을 설치하고 알림을 켠 환자에게 치과 이름으로 ${SEND_TIME_TEXT}에 나갑니다. 상한: ${LIMITS_TEXT}.`}
            </div>
            {!canSend && <div style={{ ...infoBox, marginTop: 8 }}>{OWNER_ONLY_TEXT}</div>}
            {canSend && nameBlocked && <div style={{ marginTop: 8 }}><RecallSendBlockers blockers={listData.blockers} /></div>}
            {canSend && paBlocked && (
                <div style={{ ...infoBox, marginTop: 8, background: '#fffbeb', borderColor: '#fde68a', color: '#92400e' }}>
                    {'치과 안내를 보내려면 치과 대표가 개정된 환자 앱 처리위탁 확인을 다시 해야 합니다. 환자 자동관리 화면 상단의 처리위탁 확인 카드에서 확인해 주세요.'}
                </div>
            )}

            <fieldset disabled={locked || !!busy} style={{ border: 'none', margin: 0, padding: 0, ...(locked ? { opacity: 0.6 } : {}) }}>
                {step(1, '안내 종류')}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
                    {NOTICE_TEMPLATES.map(t => (
                        <label key={t.code} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', border: `1px solid ${code === t.code ? '#6366f1' : '#e2e8f0'}`, background: code === t.code ? '#eef2ff' : '#fff', borderRadius: 8, padding: '8px 10px', cursor: locked ? 'not-allowed' : 'pointer' }}>
                            <input type="radio" name="notice-template" checked={code === t.code} onChange={() => setCode(t.code)} style={{ marginTop: 3 }} />
                            <span><span style={{ fontSize: 13.5, fontWeight: 600, color: '#0f172a', display: 'block' }}>{t.label}</span><span style={{ fontSize: 12, color: '#64748b' }}>{t.desc}</span></span>
                        </label>
                    ))}
                </div>

                {code && (
                    <>
                        {step(2, '날짜·시간')}
                        <NoticeVarsForm code={code} vars={vars} setVars={setVars} disabled={locked || !!busy} todayKey={todayKey} prepareOptions={listData?.prepareOptions} />

                        {step(3, '받는 환자')}
                        {isVisit ? (
                            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 6 }}>{'예약 확인은 환자 1명에게만 보냅니다.'}</div>
                        ) : (
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                                {AUDIENCE_MODES.map(a => <button key={a.mode} type="button" onClick={() => setMode(a.mode)} style={chip(mode === a.mode)}>{a.label}</button>)}
                            </div>
                        )}
                        {!isVisit && mode === 'recent' && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 13, color: '#334155' }}>{'최근'}</span>
                                <select value={months} onChange={(e) => setMonths(Number(e.target.value))} style={input} aria-label="최근 방문 개월 수">
                                    {RECENT_MONTH_OPTIONS.map(m => <option key={m} value={m}>{`${m}개월`}</option>)}
                                </select>
                                <span style={{ fontSize: 13, color: '#334155' }}>{'안에 등록했거나 의뢰서가 연결된 환자(진료 방문 기록은 아직 반영되지 않습니다)'}</span>
                            </div>
                        )}
                        {!isVisit && mode === 'all' && <div style={{ fontSize: 12.5, color: '#64748b' }}>{'명부에 등록된 환자 중 알림을 켠 환자에게 갑니다. 오래 방문하지 않은 환자까지 포함되므로, 보통은 「최근 환자」를 권합니다.'}</div>}
                        {(isVisit || mode === 'pick') && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => setPickerOpen(true)} style={U.buttonSecondary}><ListChecks size={14} /> {picked.length ? '다시 고르기' : '환자 고르기'}</button>
                                <span style={{ fontSize: 13, color: '#475569' }}>{isVisit ? (picked[0] ? `${picked[0].name || '(이름 없음)'}` : '선택 없음') : `선택 ${picked.length}명(최대 ${PICK_MAX}명)`}</span>
                            </div>
                        )}

                        {step(4, '발송일')}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <input type="date" value={sendDay} min={earliest} max={maxSend} onChange={(e) => setSendDay(e.target.value)} style={input} aria-label="발송일" />
                            <span style={{ fontSize: 13, color: '#475569' }}>{`${SEND_TIME_TEXT} 발송(시각은 바꿀 수 없습니다)`}</span>
                        </div>
                        {code === 'notice_closed' && (
                            <div style={{ fontSize: 12.5, color: closedLate ? '#92400e' : '#64748b', marginTop: 6, lineHeight: 1.5 }}>
                                {'휴진 안내 발송일은 휴진 시작 3일 전 이상으로 잡아 주세요. 같은 날 환자에게 다른 안내가 먼저 나가면 다음 진료일로 미뤄지고, 3일 넘게 밀리면 보내지 않습니다.'}
                            </div>
                        )}

                        {formErr && <div style={{ fontSize: 12.5, color: '#64748b', marginTop: 10 }}>{formErr}</div>}
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                            <button type="button" onClick={doPreview} disabled={locked || !!busy || !!formErr} title={!canSend ? OWNER_ONLY_TEXT : undefined}
                                style={{ ...U.buttonSecondary, ...((locked || busy || formErr) ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>{busy === 'preview' ? '미리보기 만드는 중...' : '미리보기'}</button>
                        </div>
                    </>
                )}
            </fieldset>

            {error && <div style={errBox}>{error}</div>}

            {preview && (
                <div style={{ marginTop: 12, border: '1px solid #c7d2fe', borderRadius: 8, padding: 12, background: '#f8fafc' }}>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>{'미리보기 — 환자가 보는 내용'}</div>
                    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{'앱 알림함·치과 소식'}</div>
                    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 12px', fontSize: 14, color: '#0f172a', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                        {preview.title && <div style={{ fontWeight: 700, marginBottom: 4 }}>{preview.title}</div>}
                        {preview.text || '(서버가 본문을 돌려주지 않았습니다 — 예약하지 말고 덴탈클러스터에 문의해 주세요)'}
                    </div>
                    {preview.lockText && (
                        <>
                            <div style={{ fontSize: 12, color: '#64748b', margin: '8px 0 2px' }}>{'잠금화면에 보이는 문구'}</div>
                            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: '#334155' }}>{preview.lockText}</div>
                        </>
                    )}
                    <div style={{ fontSize: 13, color: '#334155', marginTop: 10, lineHeight: 1.7 }}>
                        <div>{`발송: ${fmtDay(preview.sendDay)} ${SEND_TIME_TEXT}`}</div>
                        <div>{`대상 ${preview.targets ?? '-'}명 · 지금 기준 받을 수 있는 환자 ${preview.sendable ?? '-'}명`}{Number.isFinite(preview.noApp) && preview.noApp > 0 ? ` (그중 앱 없음 ${preview.noApp}명 — 보낼 때 건너뜀)` : ''}</div>
                        {preview.skipped.length > 0 && <div>{'제외: '}{preview.skipped.map(s => `${s.label} ${s.n}명`).join(' · ')}</div>}
                        {code !== 'notice_visit' && Number.isFinite(preview.caps?.monthUsed) && Number.isFinite(preview.caps?.monthMax) && <div style={{ fontSize: 12.5, color: '#64748b' }}>{`이번 달 안내 ${preview.caps.monthUsed}/${preview.caps.monthMax}건 사용(예약 확인 제외)`}</div>}
                        {preview.closedShortLead && <div style={{ fontSize: 12.5, color: '#92400e' }}>{'발송일이 휴진 시작 3일 전보다 늦습니다. 다른 안내에 밀리면 휴진 전에 전달되지 못할 수 있습니다.'}</div>}
                        {preview.capBlocks.map((m, i) => <div key={i} style={{ fontSize: 12.5, color: '#92400e', fontWeight: 600 }}>{m}</div>)}
                        <div style={{ fontSize: 12.5, color: '#64748b' }}>{'보내기 직전에 환자별로 다시 확인하므로 실제 전달 수는 달라질 수 있습니다. 보낸 뒤에는 회수할 수 없습니다.'}</div>
                    </div>
                    {!preview.text && <div style={{ fontSize: 12.5, color: '#92400e', marginTop: 6 }}>{'본문이 확인되지 않아 예약 버튼을 잠갔습니다.'}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <button type="button" onClick={doSchedule} disabled={locked || !!busy || !preview.text || preview.capBlocks.length > 0}
                            style={{ ...U.buttonPrimary, ...((locked || busy || !preview.text || preview.capBlocks.length > 0) ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>
                            <Send size={14} /> {busy === 'schedule' ? '예약 중...' : `${fmtDay(preview.sendDay)} 발송 예약`}
                        </button>
                        <button type="button" onClick={() => setPreview(null)} disabled={!!busy} style={U.buttonSecondary}>{'고치기'}</button>
                    </div>
                </div>
            )}
            {done && <div style={okBox}>{done}</div>}

            {pickerOpen && (
                <ClinicPatientMultiPicker clinicId={clinicId} title={isVisit ? '예약 확인을 보낼 환자 1명' : '안내를 보낼 환자 고르기'} max={isVisit ? 1 : PICK_MAX} single={isVisit}
                    statusOf={pickStatus} initialSelectedIds={picked.map(r => r.id)}
                    hint="알림을 끈 환자·앱이 없는 환자는 골라도 보내지 않습니다(미리보기에 건수로 표시)."
                    onClose={() => setPickerOpen(false)} onConfirm={(rows) => { setPicked(rows); setPickerOpen(false); }} />
            )}
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// C 현황 — 링크 3개 + 「보낸 안내」 표(clinicNotify 일 때)
// ─────────────────────────────────────────────────────────────────────────────
function NoticeStatusTable({ isOwner, listState }) {
    const [busyId, setBusyId] = useState('');
    const [error, setError] = useState('');
    const d = listState.data;
    const canCancel = isOwner && d?.canSend !== false;   // 행별로는 서버 canCancel(scheduled·미발송)도 함께 본다

    const guard = useInFlight();
    const cancel = (row) => guard(async () => {
        if (!canCancel || busyId) return;
        if (!window.confirm(`${fmtDay(row.sendDay)} 「${row.label}」 안내 예약을 취소할까요?`)) return;
        setBusyId(row.noticeId); setError('');
        try {
            const r = await cancelClinicNotice(row.noticeId);
            if (r?.ok === false) throw new Error('취소 응답이 올바르지 않습니다');
            listState.reload();
        } catch (e) {
            setError(describeNotifyError(e, '취소하지 못했습니다. 잠시 후 다시 시도해 주세요.'));
        } finally { setBusyId(''); }
    });

    return (
        <div style={{ ...U.card, marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <h3 style={{ ...subTitle, margin: 0 }}>{'보낸 안내(최근 90일)'}</h3>
                <button type="button" onClick={listState.reload} disabled={listState.loading} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, marginLeft: 'auto' }}><RefreshCw size={12} /> {'새로고침'}</button>
            </div>
            {Number.isFinite(d?.monthUsed) && Number.isFinite(d?.monthLimit) && <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 6 }}>{`이번 달 ${d.monthUsed}/${d.monthLimit}건 사용(예약 확인 제외)`}</div>}
            {listState.error && <div style={errBox}>{listState.error} <button type="button" onClick={listState.reload} style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, marginLeft: 6 }}>다시 시도</button></div>}
            {error && <div style={errBox}>{error}</div>}
            {listState.loading && !d ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>불러오는 중...</div>
            ) : !d ? null : d.items.length === 0 ? (
                <div style={{ fontSize: 13, color: '#94a3b8', padding: '8px 0' }}>{'최근 90일 동안 예약하거나 보낸 안내가 없습니다.'}</div>
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr><th style={thStyle}>발송일</th><th style={thStyle}>종류</th><th style={thStyle}>대상</th><th style={thStyle}>전달</th><th style={thStyle}>미전달 사유</th><th style={thStyle}>상태</th><th style={thStyle}></th></tr>
                        </thead>
                        <tbody>
                            {d.items.map(r => (
                                <tr key={r.noticeId}>
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{r.sendDay ? fmtDay(r.sendDay) : '-'}{r.deferredDays > 0 ? <div style={{ fontSize: 12, color: '#64748b' }}>{`${r.deferredDays}일 미뤄짐`}</div> : null}</td>
                                    <td style={tdStyle}>{r.label}{r.audienceText && <div style={{ fontSize: 12, color: '#64748b' }}>{r.audienceText}</div>}</td>
                                    <td style={tdStyle}>{r.targets == null ? '-' : `${r.targets}명`}</td>
                                    <td style={tdStyle}>{r.sent == null ? '-' : `${r.sent}명`}{r.pending > 0 && r.status !== 'scheduled' ? <div style={{ fontSize: 12, color: '#64748b' }}>{`남은 ${r.pending}명`}</div> : null}</td>
                                    <td style={{ ...tdStyle, fontSize: 12.5 }}>{r.skipped.length ? r.skipped.map(s => `${s.label} ${s.n}`).join(' · ') : '-'}</td>
                                    <td style={tdStyle}>{r.statusLabel}{r.status === 'expired' && r.expiredReason ? <div style={{ fontSize: 12, color: '#64748b' }}>{r.expiredReason === 'not_delivered' ? '기간 안에 전달하지 못함' : r.expiredReason === 'held_expired' ? '발송 보류 중 기한 지나 종료' : r.expiredReason}</div> : null}</td>
                                    <td style={tdStyle}>
                                        {r.status === 'scheduled' && r.canCancel && (
                                            <button type="button" onClick={() => cancel(r)} disabled={!canCancel || !!busyId} title={!canCancel ? OWNER_ONLY_TEXT : undefined}
                                                style={{ ...U.buttonSecondary, padding: '3px 10px', fontSize: 12, ...((!canCancel || busyId) ? { opacity: 0.55, cursor: 'not-allowed' } : {}) }}>{busyId === r.noticeId ? '취소 중...' : '취소'}</button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
            {d?.truncated && <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>{'표시 한도(100건)를 넘어 오래된 안내 일부는 보이지 않습니다.'}</div>}
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>{'예약은 보내기 전까지만 취소할 수 있고, 보낸 안내는 회수할 수 없습니다. 환자별 결과는 환자 상세의 「보낸 소식」에서 봅니다.'}</div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// 페이지
// ─────────────────────────────────────────────────────────────────────────────
function ClinicAlerts({ user }) {
    const location = useLocation();
    const clinicId = user?.companyId || user?.uid;
    const isOwner = !user?.companyId;

    const [flags, setFlags] = useState(null);   // { recall, notify } — 판정 전 null(블록 비노출·CF 호출 0)
    const [defState, setDefState] = useState({ loading: false, error: '', data: null });
    const [listState, setListState] = useState({ loading: false, error: '', data: null });
    const [blockers, setBlockers] = useState([]);

    useEffect(() => {
        let alive = true;
        getFeatures()
            .then(f => { if (alive) setFlags({ recall: isClinicRecallOn(f, user, user?.email), notify: isClinicNotifyOn(f, user, user?.email) }); })
            .catch(e => { console.error('[알림 파트] 플래그 조회 실패:', e?.code || e?.message || e); if (alive) setFlags({ recall: false, notify: false }); });
        return () => { alive = false; };
    }, [user]);

    const loadDefaults = useCallback(async () => {
        setDefState(s => ({ ...s, loading: true, error: '' }));
        try {
            const r = await getClinicRecallDefaults();
            setDefState({ loading: false, error: '', data: r });
            setBlockers(clinicSendBlockers(r.raw));
        } catch (e) {
            setDefState({ loading: false, error: describeRecallDefaultsError(e, '기본 설정을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'), data: null });
        }
    }, []);
    const loadNotices = useCallback(async () => {
        setListState(s => ({ ...s, loading: true, error: '' }));
        try {
            const r = await listClinicNotices();
            setListState({ loading: false, error: '', data: r });
        } catch (e) {
            setListState(s => ({ loading: false, error: describeNotifyError(e, '보낸 안내를 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'), data: s.data }));
        }
    }, []);

    useEffect(() => { if (flags?.recall) loadDefaults(); }, [flags?.recall, loadDefaults]);
    useEffect(() => { if (flags?.notify) loadNotices(); }, [flags?.notify, loadNotices]);

    // #recall · #notice · #status 착지(② "설정" 링크 등)
    useEffect(() => {
        if (!flags) return;
        const id = (location.hash || '').replace('#', '');
        if (!id) return;
        const t = setTimeout(() => { const el = document.getElementById(id); if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 80);
        return () => clearTimeout(t);
    }, [flags, location.hash]);

    const defStateWithReload = { ...defState, reload: loadDefaults };
    const listStateWithReload = { ...listState, reload: loadNotices };

    return (
        <div style={U.content}>
            <PageHeader icon={Bell} title="알림" subtitle="검진 시기 안내의 기본값과 대상, 치과 안내 발송 — 환자 앱으로 치과 이름으로 나갑니다"
                actions={<Link to="/patients" style={{ ...U.buttonSecondary, textDecoration: 'none' }}><ArrowLeft size={14} /> {'환자 자동관리'}</Link>} />

            {!flags ? (
                <div style={{ ...U.emptyState, padding: '40px 0' }}>불러오는 중...</div>
            ) : (!flags.recall && !flags.notify) ? (
                <div style={U.emptyState}>{'알림 기능이 아직 열리지 않았습니다.'}</div>
            ) : (
                <>
                    {flags.recall && (
                        <section id="recall" style={{ marginBottom: 18, paddingTop: 12 }}>
                            <h2 style={sectionTitle}><CalendarClock size={18} color="#6366f1" /> {'A. 검진 시기 안내'}</h2>
                            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>{'정기 점검 등 다시 오실 시기를 환자 앱으로 알립니다.'}</div>
                            <RecallSendBlockers blockers={blockers} />
                            <RecallDefaultsCard isOwner={isOwner} state={defStateWithReload} onSaved={loadDefaults} />
                            <RecallBulkCard clinicId={clinicId} state={defStateWithReload} onBlockers={(b) => { if (b?.length) setBlockers(b); }} />
                        </section>
                    )}

                    {flags.notify && (
                        <section id="notice" style={{ marginBottom: 18, paddingTop: 12 }}>
                            <h2 style={sectionTitle}><Send size={18} color="#6366f1" /> {'B. 치과 안내 보내기'}</h2>
                            <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>{'휴진·진료시간 변경처럼 치과 운영에 관한 안내를 정해진 문안으로 보냅니다. 환자는 받기만 하고 답장할 수 없습니다.'}</div>
                            <NoticeSendCard clinicId={clinicId} isOwner={isOwner} listState={listStateWithReload} onScheduled={loadNotices} />
                        </section>
                    )}

                    <section id="status" style={{ marginBottom: 18, paddingTop: 12 }}>
                        <h2 style={sectionTitle}><ListChecks size={18} color="#6366f1" /> {'C. 현황'}</h2>
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                            <Link to="/patients?tab=notices" style={{ ...U.buttonSecondary, textDecoration: 'none' }}>{'발송 예정(환자 자동관리 ②)'}</Link>
                            {flags.recall && <Link to="/clinic-recalls" style={{ ...U.buttonSecondary, textDecoration: 'none' }}>{'재내원 안내 현황'}</Link>}
                        </div>
                        <div style={{ fontSize: 12.5, color: '#64748b', marginBottom: 10 }}>{'환자 한 명의 결과는 환자 상세 화면의 「보낸 소식」에서 봅니다.'}</div>
                        {flags.notify && <NoticeStatusTable isOwner={isOwner} listState={listStateWithReload} />}
                    </section>
                </>
            )}
        </div>
    );
}

export default ClinicAlerts;
